"use client"
import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import dynamic from "next/dynamic"
import { useState } from "react"
import { mediaQueries, sizes } from "ui/theme/theme"

// Dynamically import ReactPlayer to disable SSR
const ReactPlayer = dynamic(() => import("react-player"), { ssr: false })

const StyledVideoContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1165px;
  border-radius: 30px;
  overflow: hidden;
  margin: 0 auto;
  height: 100%;
  ${mediaQueries.mobile} {
    max-width: 100%;
    border-radius: 12px;
  }
  ${mediaQueries.tablet} {
    max-width: 100%;
    border-radius: 12px;
  }
`

const PlayerContainer = styled(Box)`
  border-radius: ${sizes.borderRadiusLarge};
  overflow: hidden;
  width: 100%;
  height: 100%;
`

const PlayButtonOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isVisible",
})<{ isVisible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

// Get origin from environment variable
const ORIGIN = process.env.ORIGIN || ""

const VideoContainer = ({
  url,
  title,
  autoplay = false,
}: {
  url: string
  title?: string
  autoplay?: boolean
}) => {
  // Initialize isPlaying based on autoplay prop
  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [showControls, setShowControls] = useState(autoplay)
  const PlayIcon = iconsMap["playIcon"]

  // Build VideoObject schema.org structured data
  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title || "Video",
    contentUrl: url,
    embedUrl: url,
    uploadDate: new Date().toISOString().split("T")[0], // Current date as fallback
    ...(ORIGIN && { url: ORIGIN }),
  }

  const handlePlayClick = () => {
    setIsPlaying(true)
    setShowControls(true)
  }

  const handlePause = () => {
    setIsPlaying(false)
  }

  const handlePlay = () => {
    setIsPlaying(true)
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setShowControls(false)
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
      />

      <StyledVideoContainer>
        <PlayerContainer>
          <ReactPlayer
            url={url}
            controls={showControls}
            playing={isPlaying}
            muted={autoplay} // Keep muted for autoplay compliance
            loop={autoplay}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            width="100%"
            height="100%"
            config={{
              youtube: {
                playerVars: {
                  autoplay: autoplay ? 1 : 0,
                  mute: autoplay ? 1 : 0,
                },
              },
              vimeo: {
                playerOptions: {
                  autoplay: autoplay,
                  muted: autoplay,
                },
              },
            }}
          />
        </PlayerContainer>
        {!isPlaying && (
          <PlayButtonOverlay isVisible={!isPlaying}>
            <IconButton onClick={handlePlayClick}>
              <PlayIcon />
            </IconButton>
          </PlayButtonOverlay>
        )}
      </StyledVideoContainer>
    </>
  )
}

export default VideoContainer
